import { authNamespaceStrings, type AuthNamespace } from './namespace';

/**
 * Два живых экземпляра сессии со строкой namespace в общем в одном окне: HMR пересоздал модуль без `stop()`,
 * модуль импортирован дважды, тест не остановил прежний экземпляр, опечатка в namespace другого приложения. Итог
 * без проверки — два канала и два таймера на один токен, чужие события выхода и ротация чужого refresh.
 */
export class AuthNamespaceConflictError extends Error {
    readonly conflicts: readonly string[];

    constructor(conflicts: readonly string[]) {
        super(
            `Строки сессии уже заняты другим экземпляром в этом окне: ${conflicts.join(', ')}. ` +
                'Остановите прежний экземпляр (stop()) или разведите namespace приложений.',
        );
        this.name = 'AuthNamespaceConflictError';
        this.conflicts = conflicts;
    }
}

export interface AuthNamespaceRegistry {
    /**
     * Занять строки namespace на время жизни экземпляра (его `start()`); возвращает освобождение для `stop()`,
     * повторный вызов которого — no-op. Строка, занятая живым экземпляром, — `AuthNamespaceConflictError`.
     */
    claim(namespace: AuthNamespace): () => void;
}

export function createAuthNamespaceRegistry(): AuthNamespaceRegistry {
    // строка без учёта регистра (имена заголовков к нему нечувствительны) → занявший экземпляр
    const owners = new Map<string, symbol>();

    return {
        claim(namespace) {
            const strings = authNamespaceStrings(namespace);
            if (strings.some((s) => !s)) throw new TypeError('В namespace сессии пустая строка.');
            const keys = [...new Set(strings.map((s) => s.toLowerCase()))];
            const conflicts = [...new Set(strings.filter((s) => owners.has(s.toLowerCase())))];
            if (conflicts.length) throw new AuthNamespaceConflictError(conflicts);

            const owner = Symbol('auth-namespace-claim');
            keys.forEach((key) => owners.set(key, owner));
            let released = false;
            return () => {
                if (released) return;
                released = true;
                keys.forEach((key) => {
                    if (owners.get(key) === owner) owners.delete(key);
                });
            };
        },
    };
}

const REALM_REGISTRY: unique symbol = Symbol.for('@srm/core/auth/namespace-registry');

type RealmHolder = typeof globalThis & { [REALM_REGISTRY]?: AuthNamespaceRegistry };

/**
 * Реестр окна. Живёт на `globalThis`, а не в модуле: две копии модуля (HMR, два бандла на странице) видят один реестр.
 * Создаётся при первом вызове, не при импорте.
 */
export function realmAuthNamespaceRegistry(): AuthNamespaceRegistry {
    const holder = globalThis as RealmHolder;
    return (holder[REALM_REGISTRY] ??= createAuthNamespaceRegistry());
}
